import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { Building2, Plus, Sparkles, Loader2, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const STATUS_BADGE = {
  operacional: { label: "🟢 Operacional", color: "bg-green-100 text-green-700" },
  crescimento: { label: "🟡 Crescimento", color: "bg-yellow-100 text-yellow-800" },
  atencao:     { label: "🔴 Atenção",     color: "bg-red-100 text-red-700" },
};

export default function Filiais() {
  const [filiais, setFiliais] = useState([]);
  const [totais, setTotais] = useState({});
  const [analise, setAnalise] = useState(null);
  const [loadingAnalise, setLoadingAnalise] = useState(false);
  const [novoOpen, setNovoOpen] = useState(false);
  const [form, setForm] = useState({ nome: "", sigla: "", estado: "", cidade: "", cnpj: "", telefone: "", email: "" });

  const carregar = async () => {
    const { data } = await api.get("/filiais/kpis/consolidado");
    setFiliais(data.filiais);
    setTotais(data.totais);
  };
  useEffect(() => { carregar(); }, []);

  const analisarIA = async () => {
    setLoadingAnalise(true);
    try {
      const { data } = await api.get("/filiais/kpis/analise-ia");
      setAnalise(data);
    } catch (e) {
      toast.error("Falha ao gerar análise");
    } finally {
      setLoadingAnalise(false);
    }
  };

  const criarFilial = async () => {
    if (!form.nome || !form.sigla || !form.estado || !form.cidade) {
      toast.error("Preencha nome, sigla, estado e cidade");
      return;
    }
    try {
      await api.post("/filiais/", form);
      toast.success("Filial criada!");
      setNovoOpen(false);
      setForm({ nome: "", sigla: "", estado: "", cidade: "", cnpj: "", telefone: "", email: "" });
      carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao criar");
    }
  };

  const toggleStatus = async (f) => {
    try {
      await api.patch(`/filiais/${f.id}`, { ativa: !f.ativa });
      toast.success(`Filial ${f.ativa ? "desativada" : "ativada"}`);
      carregar();
    } catch (e) {
      toast.error("Falha ao alterar status");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>
            Filiais
          </h1>
          <p className="text-sm text-muted-foreground">
            Visão estratégica multi-regional · {filiais.length} filiais ativas
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={analisarIA}
            disabled={loadingAnalise}
            data-testid="filiais-btn-analise-ia"
            className="text-white"
            style={{ background: "#1B3A6B" }}
          >
            {loadingAnalise ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Sparkles size={14} className="mr-2" />}
            Análise estratégica IA
          </Button>
          <Dialog open={novoOpen} onOpenChange={setNovoOpen}>
            <DialogTrigger asChild>
              <Button data-testid="filiais-btn-nova" className="text-white" style={{ background: "#F97316" }}>
                <Plus size={14} className="mr-2" /> Nova filial
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nova filial</DialogTitle>
                <DialogDescription>
                  Cadastre uma nova filial. O isolamento de dados é automático após a criação.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Sigla *</Label>
                    <Input
                      data-testid="filiais-form-sigla"
                      value={form.sigla}
                      onChange={(e) => setForm({ ...form, sigla: e.target.value.toUpperCase().slice(0, 2) })}
                      placeholder="PE"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Estado *</Label>
                    <Input
                      data-testid="filiais-form-estado"
                      value={form.estado}
                      onChange={(e) => setForm({ ...form, estado: e.target.value })}
                      placeholder="Pernambuco"
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Nome *</Label>
                  <Input
                    data-testid="filiais-form-nome"
                    value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                    placeholder="Filial Pernambuco"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Cidade sede *</Label>
                  <Input
                    data-testid="filiais-form-cidade"
                    value={form.cidade}
                    onChange={(e) => setForm({ ...form, cidade: e.target.value })}
                    placeholder="Recife"
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>CNPJ</Label>
                    <Input
                      value={form.cnpj}
                      onChange={(e) => setForm({ ...form, cnpj: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Telefone</Label>
                    <Input
                      value={form.telefone}
                      onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
                <Button
                  onClick={criarFilial}
                  data-testid="filiais-form-salvar"
                  className="w-full text-white"
                  style={{ background: "#F97316" }}
                >
                  Criar filial
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Análise IA */}
      {analise && (
        <Card className="p-5 border-l-4" style={{ borderLeftColor: "#F97316" }} data-testid="filiais-analise-card">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={16} className="text-orange-500" />
            <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: "#1B3A6B" }}>
              Inteligência estratégica
            </h2>
            <span className="text-xs text-gray-400 ml-auto">
              {new Date(analise.gerada_em).toLocaleString("pt-BR")}
            </span>
          </div>
          <div className="text-sm prose prose-sm max-w-none whitespace-pre-line">
            {analise.analise}
          </div>
        </Card>
      )}

      {/* Cards de filiais */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="filiais-grid">
        {filiais.map((f) => {
          const st = STATUS_BADGE[f.kpis.status] || STATUS_BADGE.crescimento;
          return (
            <Card
              key={f.id}
              className="p-5 hover:shadow-md transition border-t-4"
              style={{ borderTopColor: f.cor }}
              data-testid={`filial-card-${f.sigla.toLowerCase()}`}
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Building2 size={16} style={{ color: f.cor }} />
                    <h3 className="font-display font-bold text-lg" style={{ color: "#1B3A6B" }}>
                      {f.sigla}
                    </h3>
                    <Badge variant="outline" className="text-[10px]">{f.cidade}</Badge>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{f.estado}</p>
                </div>
                <Badge className={st.color + " text-xs"}>{st.label}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <Metric label="MRR" value={fmt(f.kpis.mrr)} highlight />
                <Metric label="Equipamentos" value={f.kpis.equipamentos} />
                <Metric label="Clientes" value={f.kpis.clientes} />
                <Metric label="Tickets" value={f.kpis.tickets_abertos} />
              </div>
              <div className="mt-3 pt-3 border-t flex items-center justify-between text-xs">
                <span className="text-gray-500">Inadimplência: <strong>{f.kpis.inadimplencia_pct}%</strong></span>
                <button
                  onClick={() => toggleStatus(f)}
                  className="text-orange-600 hover:text-orange-700 font-medium"
                  data-testid={`filial-toggle-${f.sigla.toLowerCase()}`}
                >
                  {f.ativa ? "Desativar" : "Ativar"}
                </button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Tabela consolidada */}
      <Card className="p-0 overflow-hidden" data-testid="filiais-tabela">
        <div className="px-5 py-3 border-b flex items-center gap-2">
          <TrendingUp size={16} className="text-orange-500" />
          <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: "#1B3A6B" }}>
            Comparativo consolidado
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left">Métrica</th>
                {filiais.map((f) => (
                  <th key={f.id} className="px-3 py-2 text-right">{f.sigla}</th>
                ))}
                <th className="px-4 py-2 text-right font-bold" style={{ color: "#1B3A6B" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              <Row label="Clientes" valores={filiais.map((f) => f.kpis.clientes)} total={totais.clientes} />
              <Row label="Equipamentos" valores={filiais.map((f) => f.kpis.equipamentos)} total={totais.equipamentos} />
              <Row label="MRR" valores={filiais.map((f) => fmt(f.kpis.mrr))} total={fmt(totais.mrr)} />
              <Row label="Inadimplência" valores={filiais.map((f) => f.kpis.inadimplencia_pct + "%")} total="—" />
              <Row label="Tickets abertos" valores={filiais.map((f) => f.kpis.tickets_abertos)} total={totais.tickets_abertos} />
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

const Metric = ({ label, value, highlight }) => (
  <div>
    <div className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</div>
    <div className={`font-bold ${highlight ? "text-orange-600" : "text-gray-800"}`}>{value}</div>
  </div>
);

const Row = ({ label, valores, total }) => (
  <tr className="border-b last:border-0 hover:bg-gray-50">
    <td className="px-4 py-2 font-medium text-gray-700">{label}</td>
    {valores.map((v, i) => (
      <td key={i} className="px-3 py-2 text-right text-gray-600">{v}</td>
    ))}
    <td className="px-4 py-2 text-right font-bold" style={{ color: "#1B3A6B" }}>{total}</td>
  </tr>
);
